import { ETableStructureAlign, ETableStructureType, TTableStructure } from 'ui/EasTable/types';
import { EInvoiceServiceType, TInvoiceListItem } from 'redux/reducers/invoiceManagement/types';
import { DATE_FILTER } from '../../ui/EasTable/constants';
import { EIconButtons } from '../../ui/EasTable/EasTableRow/EasTableValue/constants';
import { COLORS } from '../../services/constants/colors';
import { PAID_THROUGH_OPTIONS } from './constants';

// Extended type with required id for table compatibility
type TInvoiceListItemWithId = TInvoiceListItem & { id: number; payment_receipt_url?: string };

/** Action: receipt of the online payment (shown only for invoices paid through the payment gateway). */
export const DOWNLOAD_PAYMENT_RECEIPT = 'Download payment receipt';

/** Normalizes backend date "2025-12-01 00:00:00+00" to ISO string for Luxon DateTime.fromISO */
function normalizeToIsoDate(s: string): string {
	return s
		.trim()
		.replace(' ', 'T')
		.replace(/([+-])(\d{2})$/, '$1$2:00');
}

/** Parses invoice_duration string e.g. "[\"2025-12-01 00:00:00+00\",\"2026-01-01 00:00:00+00\")" to "date1 - date2" for DATE_PERIOD display */
export const parseInvoiceDurationToPeriodString = (
	raw: string | null | undefined,
): string | null => {
	if (!raw || typeof raw !== 'string') return null;
	// Support both escaped \"...\" (from JSON) and plain "..." quotes
	const matches = raw.match(/\\?"([^"]+)\\?"/g) ?? raw.match(/"([^"]+)"/g);
	if (!matches || matches.length < 2) return null;
	const from = matches[0].replace(/^\\?"|\\?"$/g, '');
	const to = matches[1].replace(/^\\?"|\\?"$/g, '');
	return `${normalizeToIsoDate(from)} - ${normalizeToIsoDate(to)}`;
};

export const tableStructure: TTableStructure<keyof TInvoiceListItemWithId>[] = [
	{
		selector: 'id',
		name: 'ID',
		show: false,
		sortable: false,
		filterable: false,
		type: ETableStructureType.NUMBER,
		align: ETableStructureAlign.RIGHT,
		colSpan: 1,
		key: true,
	},

	{
		selector: 'invoice_id',
		name: 'Invoice ID',
		show: false,
		sortable: true,
		filterable: false,
		type: ETableStructureType.STRING,
		align: ETableStructureAlign.LEFT,
		colSpan: 1,
		showInFoolData: true,
	},
	{
		selector: 'invoice_number',
		name: 'Invoice Number',
		show: true,
		sortable: true,
		filterable: false,
		searchable: true,
		type: ETableStructureType.LINK,
		align: ETableStructureAlign.LEFT,
		colSpan: 1,
		showInFoolData: true,
	},
	{
		selector: 'invoice_date',
		name: 'Invoice Date',
		show: true,
		sortable: true,
		filterable: true,
		type: ETableStructureType.DATE,
		align: ETableStructureAlign.LEFT,
		filter: DATE_FILTER,
		colSpan: 1,
		showInFoolData: true,
	},
	{
		selector: 'em_id',
		name: 'EM ID',
		show: false,
		sortable: true,
		filterable: false,
		type: ETableStructureType.NUMBER,
		align: ETableStructureAlign.LEFT,
		colSpan: 1,
	},

	{
		selector: 'client',
		name: 'Client',
		show: true,
		sortable: true,
		filterable: false,
		type: ETableStructureType.EM,
		value: (item: TInvoiceListItemWithId) => {
			return { id: item.em_id, name: item.client };
		},
		align: ETableStructureAlign.LEFT,
		colSpan: 1,
		showInFoolData: true,
	},
	{
		selector: 'email',
		name: 'Company Email',
		show: false,
		sortable: true,
		filterable: false,
		type: ETableStructureType.EMAIL,
		align: ETableStructureAlign.LEFT,
		colSpan: 1,
		showInFoolData: true,
	},

	{
		selector: 'reference_number',
		name: 'Reference Number',
		show: false,
		sortable: true,
		filterable: false,
		type: ETableStructureType.NUMBER,
		align: ETableStructureAlign.LEFT,
		colSpan: 1,
		showInFoolData: true,
	},
	{
		selector: 'client_number',
		name: 'Client Number',
		show: false,
		sortable: true,
		filterable: false,
		type: ETableStructureType.STRING,
		align: ETableStructureAlign.LEFT,
		colSpan: 1,
		showInFoolData: true,
	},
	{
		selector: 'country',
		name: 'Country',
		show: false,
		sortable: true,
		filterable: false,
		type: ETableStructureType.COUNTRY_NAME,
		align: ETableStructureAlign.LEFT,
		colSpan: 1,
		showInFoolData: true,
	},
	{
		selector: 'invoice_duration',
		name: 'Invoice Duration',
		show: false,
		sortable: true,
		filterable: false,
		type: ETableStructureType.DATE_PERIOD,
		align: ETableStructureAlign.LEFT,
		value: (item: TInvoiceListItemWithId) =>
			parseInvoiceDurationToPeriodString(item.invoice_duration) ?? '',
		colSpan: 1,
		showInFoolData: true,
	},
	{
		selector: 'due_date',
		name: 'Due Date',
		show: false,
		sortable: true,
		filterable: false,
		type: ETableStructureType.DATE,
		align: ETableStructureAlign.LEFT,
		filter: DATE_FILTER,
		colSpan: 1,
		showInFoolData: true,
	},
	{
		selector: 'timestamp',
		name: 'Timestamp',
		show: false,
		sortable: true,
		filterable: false,
		type: ETableStructureType.DATE,
		align: ETableStructureAlign.LEFT,
		filter: DATE_FILTER,
		colSpan: 1,
		showInFoolData: true,
	},
	{
		selector: 'invoiced_amount_in_eur',
		name: 'Invoiced Amount',
		show: true,
		sortable: true,
		filterable: false,
		type: ETableStructureType.AMOUNT,
		align: ETableStructureAlign.RIGHT,
		colSpan: 1,
		showInFoolData: true,
	},
	{
		selector: 'invoice_carry_over_amount',
		name: 'Invoice Carry Over Amount',
		show: true,
		sortable: true,
		filterable: false,
		type: ETableStructureType.AMOUNT,
		align: ETableStructureAlign.RIGHT,
		colSpan: 1,
		showInFoolData: true,
	},
	{
		selector: 'total_invoice_amount_in_eur',
		name: 'Total Amount',
		show: true,
		sortable: true,
		filterable: true,
		type: ETableStructureType.AMOUNT,
		align: ETableStructureAlign.RIGHT,
		colSpan: 1,
		showInFoolData: true,
	},

	{
		selector: 'paid_amount',
		name: 'Paid Amount',
		show: true,
		sortable: true,
		filterable: false,
		type: ETableStructureType.AMOUNT,
		align: ETableStructureAlign.RIGHT,
		colSpan: 1,
		showInFoolData: true,
	},
	{
		selector: 'vat_due_to_eas',
		name: 'VAT Due to EAS',
		show: true,
		sortable: true,
		filterable: false,
		type: ETableStructureType.AMOUNT,
		align: ETableStructureAlign.RIGHT,
		colSpan: 1,
		showInFoolData: true,
	},
	{
		selector: 'eas_fee_with_vat_on_eas_fee',
		name: 'EAS Fee with VAT on EAS Fee',
		show: true,
		sortable: true,
		filterable: false,
		type: ETableStructureType.AMOUNT,
		align: ETableStructureAlign.RIGHT,
		colSpan: 1,
		showInFoolData: true,
	},

	{
		selector: 'vat_number',
		name: 'VAT Number',
		show: false,
		sortable: true,
		filterable: false,
		type: ETableStructureType.STRING,
		align: ETableStructureAlign.LEFT,
		colSpan: 1,
		showInFoolData: true,
	},
	{
		selector: 'vat_rate',
		name: 'VAT Rate',
		show: false,
		sortable: true,
		filterable: false,
		type: ETableStructureType.AMOUNT,
		align: ETableStructureAlign.RIGHT,
		colSpan: 1,
		showInFoolData: true,
	},
	{
		selector: 'client_currency',
		name: 'Client Currency',
		show: false,
		sortable: true,
		filterable: false,
		type: ETableStructureType.STRING,
		align: ETableStructureAlign.RIGHT,
		colSpan: 1,
		showInFoolData: true,
	},

	{
		selector: 'total_invoice_amount_in_client_currency',
		name: 'Total Amount (Client Currency)',
		show: false,
		sortable: true,
		filterable: false,
		type: ETableStructureType.AMOUNT,
		currency: (item: TInvoiceListItemWithId) => item.client_currency ?? 'EUR',
		align: ETableStructureAlign.RIGHT,
		colSpan: 1,
		showInFoolData: true,
	},
	{
		selector: 'invoiced_amount_in_client_currency',
		name: 'Invoiced Amount (Client Currency)',
		show: false,
		sortable: true,
		filterable: false,
		type: ETableStructureType.AMOUNT,
		currency: (item: TInvoiceListItemWithId) => item.client_currency ?? 'EUR',
		align: ETableStructureAlign.RIGHT,
		colSpan: 1,
		showInFoolData: true,
	},
	{
		selector: 'carry_over_amount',
		name: 'Carry Over Amount',
		show: false,
		sortable: true,
		filterable: false,
		type: ETableStructureType.AMOUNT,
		align: ETableStructureAlign.RIGHT,
		colSpan: 1,
		showInFoolData: true,
	},
	{
		selector: 'due_interest_amount',
		name: 'Due Interest Amount',
		show: false,
		sortable: true,
		filterable: false,
		type: ETableStructureType.AMOUNT,
		align: ETableStructureAlign.RIGHT,
		colSpan: 1,
		showInFoolData: true,
	},
	{
		selector: 'eas_fee_with_invoice_fee',
		name: 'EAS Fee with Invoice Fee',
		show: false,
		sortable: true,
		filterable: false,
		type: ETableStructureType.AMOUNT,
		align: ETableStructureAlign.RIGHT,
		colSpan: 1,
		showInFoolData: true,
	},
	{
		selector: 'vat_on_eas_fee_and_invoicing_fee',
		name: 'VAT on EAS Fee and Invoicing Fee',
		show: false,
		sortable: true,
		filterable: false,
		type: ETableStructureType.AMOUNT,
		align: ETableStructureAlign.RIGHT,
		colSpan: 1,
		showInFoolData: true,
	},
	{
		selector: 'shipment_fee_due',
		name: 'Shipment Fee Due',
		show: false,
		sortable: true,
		filterable: false,
		type: ETableStructureType.AMOUNT,
		align: ETableStructureAlign.RIGHT,
		colSpan: 1,
		showInFoolData: true,
	},
	{
		selector: 'customs_duty_due',
		name: 'Customs Duty Due',
		show: false,
		sortable: true,
		filterable: false,
		type: ETableStructureType.AMOUNT,
		align: ETableStructureAlign.RIGHT,
		colSpan: 1,
		showInFoolData: true,
	},
	{
		selector: 'invoicing_fee_name',
		name: 'Invoicing Fee Name',
		show: false,
		sortable: true,
		filterable: false,
		type: ETableStructureType.STRING,
		align: ETableStructureAlign.LEFT,
		colSpan: 1,
		showInFoolData: true,
	},
	{
		selector: 'invoicing_fee',
		name: 'Invoicing Fee',
		show: false,
		sortable: true,
		filterable: false,
		type: ETableStructureType.AMOUNT,
		align: ETableStructureAlign.RIGHT,
		colSpan: 1,
		showInFoolData: true,
	},
	{
		selector: 'monthly_fee',
		name: 'Monthly subscription',
		show: false,
		sortable: true,
		filterable: false,
		type: ETableStructureType.AMOUNT,
		align: ETableStructureAlign.RIGHT,
		colSpan: 1,
		showInFoolData: true,
	},
	{
		selector: 'non_union_plan_amount',
		name: 'Non Union Plan Amount',
		show: false,
		sortable: true,
		filterable: false,
		type: ETableStructureType.AMOUNT,
		align: ETableStructureAlign.RIGHT,
		colSpan: 1,
		showInFoolData: true,
	},
	{
		selector: 'non_union_plan_name',
		name: 'Non Union Plan Name',
		show: false,
		sortable: true,
		filterable: false,
		type: ETableStructureType.STRING,
		align: ETableStructureAlign.LEFT,
		colSpan: 1,
		showInFoolData: true,
	},
	{
		selector: 'revolut_invoiced_amount',
		name: 'Payment provider charged (EUR)',
		show: false,
		sortable: true,
		filterable: false,
		type: ETableStructureType.AMOUNT,
		align: ETableStructureAlign.RIGHT,
		colSpan: 1,
		showInFoolData: true,
	},
	{
		selector: 'revolut_received_amount',
		name: 'Payment provider received (EUR)',
		show: false,
		sortable: true,
		filterable: false,
		type: ETableStructureType.AMOUNT,
		align: ETableStructureAlign.RIGHT,
		colSpan: 1,
		showInFoolData: true,
	},
	{
		selector: 'revolut_fee',
		name: 'Payment provider fee (EUR)',
		show: false,
		sortable: true,
		filterable: false,
		type: ETableStructureType.AMOUNT,
		align: ETableStructureAlign.RIGHT,
		colSpan: 1,
		showInFoolData: true,
	},
	{
		selector: 'scheme_list',
		name: 'Scheme List',
		show: true,
		sortable: true,
		filterable: false,
		type: ETableStructureType.STATUS_ARRAY,
		align: ETableStructureAlign.LEFT,
		filter: [
			['union', 'Union'],
			['ioss', 'IOSS'],
			['non_union', 'Non-Union'],
			['non-eu-import', 'Non-EU Import'],
			['fc-rep', 'FC-Rep'],
		],
		colSpan: 1,
		showInFoolData: true,
	},
	{
		selector: 'has_reimbursments',
		name: 'Reimbursments included',
		show: false,
		sortable: true,
		filterable: true,
		type: ETableStructureType.BOOLEAN,
		align: ETableStructureAlign.CENTER,
		colSpan: 1,
		filter: [
			['true', 'Yes'],
			['false', 'No'],
		],
		showInFoolData: true,
	},
	{
		selector: 'reimbursed_amount',
		name: 'Reimbursed Amount',
		show: false,
		sortable: true,
		filterable: false,
		type: ETableStructureType.AMOUNT,
		align: ETableStructureAlign.RIGHT,
		colSpan: 1,
		showInFoolData: true,
	},
	{
		selector: 'vat_reimbursement_list_import',
		name: 'VAT Reimbursement List Import',
		show: false,
		sortable: true,
		filterable: false,
		type: ETableStructureType.ARRAY_OF_OBJECTS,
		align: ETableStructureAlign.LEFT,
		colSpan: 1,
		showInFoolData: true,
	},

	{
		selector: 'year',
		name: 'Year',
		show: false,
		sortable: true,
		filterable: false,
		type: ETableStructureType.NUMBER,
		align: ETableStructureAlign.RIGHT,
		colSpan: 1,
		showInFoolData: true,
	},
	{
		selector: 'month',
		name: 'Month',
		show: false,
		sortable: true,
		filterable: false,
		type: ETableStructureType.NUMBER,
		align: ETableStructureAlign.RIGHT,
		colSpan: 1,
		showInFoolData: true,
	},
	{
		selector: 'zero_invoice',
		name: 'Zero Invoice',
		show: false,
		sortable: true,
		filterable: true,
		type: ETableStructureType.BOOLEAN,
		align: ETableStructureAlign.CENTER,
		colSpan: 1,
		filter: [
			['true', 'Yes'],
			['false', 'No'],
		],
		showInFoolData: true,
	},
	{
		selector: 'is_gpsr',
		name: 'Is GPSR',
		show: false,
		sortable: true,
		filterable: false,
		type: ETableStructureType.BOOLEAN,
		align: ETableStructureAlign.CENTER,
		filter: [
			['true', 'Yes'],
			['false', 'No'],
		],
		colSpan: 1,
		showInFoolData: true,
	},
	{
		selector: 'service_type',
		name: 'Service Type',
		show: true,
		sortable: true,
		filterable: true,
		type: ETableStructureType.STATUS,
		align: ETableStructureAlign.LEFT,
		colSpan: 1,
		filter: [
			[EInvoiceServiceType.ADDONS, 'Addons'],
			[EInvoiceServiceType.GPSR, 'GPSR'],
			[EInvoiceServiceType.EAS, 'EAS'],
			[EInvoiceServiceType.SWISS, 'Swiss'],
			[EInvoiceServiceType.EUDR, 'EUDR'],
			[EInvoiceServiceType.EU_DDP, 'EU DDP'],
		],
		showInFoolData: true,
	},

	{
		selector: 'comment',
		name: 'Comment',
		show: true,
		sortable: false,
		filterable: false,
		type: ETableStructureType.TEXT,
		align: ETableStructureAlign.LEFT,
		colSpan: 1,
		showInFoolData: true,
	},

	{
		selector: 'legal_status_with_reg_country',
		name: 'Legal Status',
		show: false,
		sortable: true,
		filterable: false,
		type: ETableStructureType.STATUS,
		align: ETableStructureAlign.LEFT,
		colSpan: 1,
		showInFoolData: true,
		filter: [
			['pending', 'Pending'],
			['overdue', 'Overdue'],
			['paid', 'Paid'],
			['partially_paid', 'Partially Paid'],
		],
	},
	{
		selector: 'date_of_payment',
		name: 'Date of Payment',
		show: true,
		sortable: true,
		filterable: true,
		type: ETableStructureType.DATE,
		align: ETableStructureAlign.LEFT,
		filter: DATE_FILTER,
		colSpan: 1,
		showInFoolData: true,
	},

	{
		selector: 'payment_status',
		name: 'Payment Status',
		show: true,
		sortable: true,
		filterable: true,
		type: ETableStructureType.STATUS,
		align: ETableStructureAlign.LEFT,
		colSpan: 1,
		filter: [
			['pending', 'Pending'],
			['overdue', 'Overdue'],
			['paid', 'Paid'],
			['partially_paid', 'Partially Paid'],
		],
		colors: [
			['pending', 'warning.light'],
			['overdue', 'error.light'],
			['paid', 'success.light'],
			['partially_paid', 'warning.light'],
		],
		showInFoolData: true,
	},
	{
		selector: 'communication_status',
		name: 'Communication Status',
		show: true,
		sortable: true,
		filterable: true,
		type: ETableStructureType.STATUS,
		align: ETableStructureAlign.LEFT,
		colSpan: 1,
		filter: [
			['pending', 'Pending'],
			['suspended', 'Suspended'],
			['sent', 'Sent'],
		],
		colors: [
			['pending', 'warning.light'],
			['suspended', 'primary.light'],
			['sent', 'success.light'],
		],
		showInFoolData: true,
	},
	{
		selector: 'non_eu_import_vat_eur',
		name: 'Non-EU Import VAT EUR',
		show: false,
		sortable: true,
		filterable: false,
		type: ETableStructureType.AMOUNT,
		align: ETableStructureAlign.RIGHT,
		colSpan: 1,
		showInFoolData: true,
	},
	{
		selector: 'vat_fiscal_import',
		name: 'IOSS VAT due to EAS',
		show: false,
		sortable: true,
		filterable: false,
		type: ETableStructureType.AMOUNT,
		align: ETableStructureAlign.RIGHT,
		colSpan: 1,
		showInFoolData: true,
	},
	{
		selector: 'vat_fiscal_union',
		name: 'UNION VAT due to EAS',
		show: false,
		sortable: true,
		filterable: false,
		type: ETableStructureType.AMOUNT,
		align: ETableStructureAlign.RIGHT,
		colSpan: 1,
		showInFoolData: true,
	},
	{
		selector: 'vat_fiscal_non_union',
		name: 'NON-UNION VAT due to EAS',
		show: false,
		sortable: true,
		filterable: false,
		type: ETableStructureType.AMOUNT,
		align: ETableStructureAlign.RIGHT,
		colSpan: 1,
		showInFoolData: true,
	},
	{
		selector: 'fc_rep_vat_eur',
		name: 'DDP Value Added Tax',
		show: false,
		sortable: true,
		filterable: false,
		type: ETableStructureType.AMOUNT,
		align: ETableStructureAlign.RIGHT,
		colSpan: 1,
		showInFoolData: true,
	},
	{
		selector: 'paid_thruogh',
		name: 'Paid Through',
		show: true,
		sortable: true,
		filterable: true,
		type: ETableStructureType.STATUS,
		align: ETableStructureAlign.RIGHT,
		colSpan: 1,
		showInFoolData: true,
		filter: PAID_THROUGH_OPTIONS.map((option) => [option.value, option.label] as [string, string]),
	},
	{
		selector: 'prepaid_amount',
		name: 'Prepaid Amount',
		show: false,
		sortable: true,
		filterable: false,
		type: ETableStructureType.AMOUNT,
		align: ETableStructureAlign.RIGHT,
		colSpan: 1,
		showInFoolData: true,
	},
	{
		selector: 'actions',
		noName: true,
		name: 'Actions',
		show: true,
		sortable: false,
		filterable: false,
		type: ETableStructureType.BUTTONS,
		align: ETableStructureAlign.LEFT,
		colSpan: 1,
		buttons: [
			{ name: 'Download invoice in PDF', icon: EIconButtons.PICTURE_AS_PDF },
			{ name: 'Download invoice in CSV', icon: EIconButtons.TABLE_CHART },
			{ name: 'Mark as paid or edit payment information', icon: EIconButtons.CHECK_CIRCLE },
			{ name: 'Add or edit comments for the invoice', icon: EIconButtons.COMMENT },
		],
		buttonFilter: (item: TInvoiceListItemWithId) => {
			const buttons = [];
			buttons.push({ name: 'Download invoice in PDF', icon: EIconButtons.PICTURE_AS_PDF });
			buttons.push({ name: 'Download invoice in CSV', icon: EIconButtons.TABLE_CHART });
			if (item.payment_receipt_url) {
				buttons.push({ name: DOWNLOAD_PAYMENT_RECEIPT, icon: EIconButtons.RECEIPT_LONG });
			}
			buttons.push({
				name: 'Mark as paid or edit payment information',
				icon: EIconButtons.CHECK_CIRCLE,
				color:
					item.payment_status === 'paid'
						? 'success.main'
						: item.payment_status === 'partially_paid'
							? 'warning.main'
							: item.payment_status === 'pending'
								? 'warning.main'
								: item.payment_status === 'overdue'
									? 'error.main'
									: 'primary.main',
				disabled: item.payment_status === 'paid' || item.payment_status === 'overdue',
			});
			buttons.push({
				name: 'Add or edit comments for the invoice',
				icon: EIconButtons.COMMENT,
				color: item.comment ? 'primary.main' : COLORS.BACKGROUND_DARK,
			});
			return buttons;
		},
	},
];
